import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import WappIcono, { TIPOS } from "../../../components/wappIcono";
import logoMenu from "../../../assets/images/logoMenu.png";
import { cargarApi, editorMovil } from "./editor";
import { peso } from "./dialogos";
import "./wappdrive.css";

const NOMBRE: Record<string, string> = { word: "WappDocs", cell: "WappCell", slide: "WappDeck", pdf: "WappPDF" };
const sinExt = (n: string) => n.replace(/\.(docx|xlsx|pptx|pdf)$/i, "");

interface Publico {
	nombre: string; tipo: string | null; ext: string; descargar: string;
	config?: any; servidor?: string; puedeEditar?: boolean; ver?: string; mime?: string; tamano?: number;
}

/**
 * Enlace público de WappCloud (/p/<token>), como "Cualquier persona con el enlace" de Google Docs: se abre sin cuenta de
 * WAPP. Documentos, hojas, presentaciones y PDF en el editor (como Invitado); fotos y videos en vista previa; lo demás
 * se descarga.
 */
const PaginaPublica: React.FC<{ token: string }> = ({ token }) => {
	const [d, setD] = useState<Publico | null>(null);
	const [error, setError] = useState<string | null>(null);
	const inst = useRef<any>(null);
	const domId = "wd-publico-editor";

	useEffect(() => {
		// Sin la app alrededor nadie pone el tema: los colores --wa-* viven en esta clase.
		document.documentElement.classList.add("theme-mode-light");
		let vivo = true;
		const movil = editorMovil();
		const accion = new URLSearchParams(window.location.search).get("a");
		axios.get(process.env.REACT_APP_API_URL_V2 + "/wappdrive/publico/" + token, { params: { movil: movil ? 1 : undefined, accion: accion || undefined } })
			.then(async (r) => {
				if (!vivo) return;
				const x: Publico = r.data;
				setD(x);
				document.title = sinExt(x.nombre) + (x.tipo ? " - " + NOMBRE[x.tipo] : "");
				if (x.tipo && x.config && x.servidor) {
					await cargarApi(x.servidor);
					if (!vivo) return;
					inst.current = new (window as any).DocsAPI.DocEditor(domId, {
						...x.config,
						events: { onMakeActionLink: (e: any) => inst.current?.setActionLink(window.location.origin + "/p/" + token + "?a=" + encodeURIComponent(JSON.stringify(e.data))) },
					});
				}
			})
			.catch((e) => vivo && setError(e?.response?.data?.message || "No se pudo abrir el enlace. Revisa tu conexión e intenta otra vez."));
		return () => { vivo = false; try { inst.current?.destroyEditor(); } catch (e) { /* ya cerrado */ } };
	}, [token]);

	const tipo = d?.tipo ? TIPOS[d.tipo] : d && /^image\//.test(d.mime || "") ? TIPOS.foto : d && /^video\//.test(d.mime || "") ? TIPOS.video : TIPOS.otro;

	return (
		<div className="wd-publico">
			<header className="wd-publico-cab">
				<img src={logoMenu} alt="Washington Academy" className="wd-publico-logo" />
				<span className="wd-publico-sep" />
				{d ? <span className="wd-tipo" style={{ background: tipo?.fondo, color: tipo?.tinta }}><WappIcono n={tipo?.icono || "file"} /></span> : null}
				<div className="wd-editor-tit">
					<b>{d ? sinExt(d.nombre) : error ? "Enlace no disponible" : "Abriendo…"}</b>
					<span>{d ? (d.tipo ? NOMBRE[d.tipo] + (d.puedeEditar ? " · cualquiera con el enlace puede editar" : " · solo lectura") : peso(d.tamano || 0)) : ""}</span>
				</div>
				<span style={{ flex: 1 }} />
				{d?.descargar ? <a className="wd-btn wd-editor-compartir" href={d.descargar}><WappIcono n="download" /> <span>Descargar</span></a> : null}
			</header>
			<main className="wd-publico-cuerpo">
				{error ? (
					<div className="wd-publico-aviso"><WappIcono n="lock" /><b>{error}</b></div>
				) : d && d.tipo ? (
					<div id={domId} />
				) : d && d.ver && /^image\//.test(d.mime || "") ? (
					<div className="wd-publico-vista"><img src={d.ver} alt={d.nombre} /></div>
				) : d && d.ver && /^video\//.test(d.mime || "") ? (
					<div className="wd-publico-vista"><video src={d.ver} controls /></div>
				) : d ? (
					<div className="wd-publico-aviso">
						<span className="wd-tipo grande" style={{ background: tipo?.fondo, color: tipo?.tinta }}><WappIcono n={tipo?.icono || "file"} /></span>
						<b>{d.nombre}</b><span>{peso(d.tamano || 0)}</span>
						<a className="wd-btn pri" href={d.descargar}><WappIcono n="download" /> Descargar</a>
					</div>
				) : null}
			</main>
		</div>
	);
};

export default PaginaPublica;
